import { ago } from "../time";
import type { Comment, Invite, Post, Profile } from "../types/social";

/**
 * Profiles, invitations and posts. The invitations matter most: they are where
 * the product's rate pacing becomes visible, so the list carries one of every
 * state including a queued invite that is waiting on the weekly budget.
 */

export const PROFILES: Profile[] = [
  {
    id: "prf_devika",
    provider: "linkedin",
    handle: "devika-menon",
    name: "Devika Menon",
    headline: "VP Revenue Operations at Lumen Freight",
    location: "London, United Kingdom",
    company: "Lumen Freight",
    title: "VP Revenue Operations",
    relation: "connected",
    connections: 3412,
    followers: 4108,
    email: "devika@lumenfreight.com",
    roles: [
      {
        company: "Lumen Freight",
        title: "VP Revenue Operations",
        period: "2023 — present",
        current: true,
      },
      {
        company: "Keystone Logistics",
        title: "Director of Sales Operations",
        period: "2019 — 2023",
        current: false,
      },
      {
        company: "Arrowhead",
        title: "Sales Operations Manager",
        period: "2016 — 2019",
        current: false,
      },
    ],
  },
  {
    id: "prf_arun",
    provider: "linkedin",
    handle: "arun-prakash-eng",
    name: "Arun Prakash",
    headline: "Staff Platform Engineer at Pallas",
    location: "Bengaluru, India",
    company: "Pallas",
    title: "Staff Platform Engineer",
    relation: "reachable",
    connections: 1890,
    roles: [
      {
        company: "Pallas",
        title: "Staff Platform Engineer",
        period: "2022 — present",
        current: true,
      },
      {
        company: "Zenith Cloud",
        title: "Senior Engineer, Infrastructure",
        period: "2018 — 2022",
        current: false,
      },
    ],
    requiresPremium: true,
  },
  {
    id: "prf_felix",
    provider: "linkedin",
    handle: "felix-brandt",
    name: "Felix Brandt",
    headline: "Head of Sales at Stadler Logistik",
    location: "Munich, Germany",
    company: "Stadler Logistik",
    title: "Head of Sales",
    relation: "connected",
    connections: 2204,
    roles: [
      {
        company: "Stadler Logistik",
        title: "Head of Sales",
        period: "2021 — present",
        current: true,
      },
    ],
  },
  {
    id: "prf_grace",
    provider: "linkedin",
    handle: "grace-lin-ops",
    name: "Grace Lin",
    headline: "Head of Partnerships at Lumen Freight",
    location: "Singapore",
    company: "Lumen Freight",
    title: "Head of Partnerships",
    relation: "reachable",
    connections: 5120,
    roles: [
      {
        company: "Lumen Freight",
        title: "Head of Partnerships",
        period: "2024 — present",
        current: true,
      },
    ],
  },
  {
    id: "prf_hiro",
    provider: "linkedin",
    handle: "hiro-tanaka-dev",
    name: "Hiro Tanaka",
    headline: "Engineering Manager at Pallas",
    location: "Tokyo, Japan",
    company: "Pallas",
    title: "Engineering Manager",
    relation: "none",
    connections: 980,
    roles: [
      {
        company: "Pallas",
        title: "Engineering Manager",
        period: "2023 — present",
        current: true,
      },
    ],
  },
];

export const INVITES: Invite[] = [
  {
    id: "ivt_grace",
    accountId: "acc_gm_maya",
    profileId: "prf_grace",
    profileName: "Grace Lin",
    note: "Grace — Devika suggested we talk about how Lumen's partners plug into your platform. Happy to share what we built for Keystone.",
    status: "sent",
    sentAt: ago(2, "d"),
  },
  {
    id: "ivt_felix",
    accountId: "acc_gm_maya",
    profileId: "prf_felix",
    profileName: "Felix Brandt",
    note: "Felix — we work with three of Stadler's peers on inbox consolidation. Worth a short conversation?",
    status: "accepted",
    sentAt: ago(4, "d"),
    respondedAt: ago(100, "m"),
  },
  {
    id: "ivt_hiro",
    accountId: "acc_gm_sofia",
    profileId: "prf_hiro",
    profileName: "Hiro Tanaka",
    note: "Hiro — hiring a Staff Platform Engineer on a team that would report into someone with your background. Open to a chat?",
    status: "queued",
    heldBecause:
      "This account has used 94 of its 100 weekly invitations. Aux0 will send it when the window resets in 22 hours.",
  },
  {
    id: "ivt_old",
    accountId: "acc_gm_sofia",
    profileId: "prf_arun",
    profileName: "Arun Prakash",
    note: "Arun — the Pallas queue migration is exactly the problem our platform team is hiring for.",
    status: "ignored",
    sentAt: ago(6, "w"),
  },
  {
    id: "ivt_withdrawn",
    accountId: "acc_ic_tom",
    profileId: "prf_hiro",
    profileName: "Hiro Tanaka",
    status: "withdrawn",
    sentAt: ago(5, "w"),
    respondedAt: ago(3, "w"),
  },
];

export const POSTS: Post[] = [
  {
    id: "pst_consolidation",
    accountId: "acc_gm_maya",
    provider: "linkedin",
    authorId: "prf_devika",
    authorName: "Devika Menon",
    text: "Counted it this morning: our SDRs touch five tools to send one sequence. Sequencer, LinkedIn automation, inbox sync, dialler, CRM. Four of them have their own idea of who the contact is.\n\nI don't want a sixth tool. I want three of these to go away.",
    at: ago(6, "d"),
    reactions: 487,
    comments: 63,
    reposts: 24,
    url: "https://www.linkedin.com/posts/devika-menon_revops",
  },
  {
    id: "pst_northwind",
    accountId: "acc_gm_maya",
    provider: "linkedin",
    authorId: "prf_felix",
    authorName: "Maya Rao",
    text: "Shipped this week: Northwind replies in the channel your buyer already chose. If they answered on WhatsApp, your rep answers on WhatsApp — same thread, same history, no copy-paste.\n\nOne API behind it. Happy to show anyone the five-minute version.",
    at: ago(2, "d"),
    reactions: 212,
    comments: 18,
    reposts: 9,
    viaApi: true,
  },
];

export const COMMENTS: Comment[] = [
  {
    id: "cmt_1",
    postId: "pst_consolidation",
    authorId: "prf_felix",
    authorName: "Felix Brandt",
    text: "The contact-identity drift is the real cost. We gave up reconciling it and just picked the CRM as truth.",
    at: ago(6, "d"),
    reactions: 31,
  },
  {
    id: "cmt_2",
    postId: "pst_consolidation",
    authorId: "prf_devika",
    authorName: "Maya Rao",
    text: "Devika — this is the exact problem we built for. Three of those five collapse into one contract and one audit trail. Sent you a note.",
    at: ago(5, "d"),
    reactions: 14,
    viaApi: true,
  },
  {
    id: "cmt_3",
    postId: "pst_northwind",
    authorId: "prf_grace",
    authorName: "Grace Lin",
    text: "Does this cover Telegram? Half our APAC partners live there.",
    at: ago(1, "d"),
    reactions: 6,
  },
];

export const PROFILE_BY_ID = new Map(PROFILES.map((profile) => [profile.id, profile]));
